import { useEffect, useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { TemplatePreview } from "./TemplatePreview";
import type { CreateTemplateInput, TemplateButton, TemplateVariableMapping } from "@/hooks/useWhatsAppMessageTemplates";
import { META_PRICING_BRL } from "@/lib/meta/pricing";

const templateSchema = z.object({
  name: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_]+$/, "Only lowercase letters, numbers, and underscores"),
  language: z.string().min(1),
  category: z.enum(["MARKETING", "UTILITY", "AUTHENTICATION"]),
  headerType: z.enum(["TEXT", "IMAGE", "VIDEO", "DOCUMENT", ""]).optional(),
  headerText: z.string().optional(),
  headerMediaUrl: z.string().url().optional().or(z.literal("")),
  bodyText: z.string().min(1).max(1024),
  footerText: z.string().max(60).optional().or(z.literal("")),
  buttons: z
    .array(
      z.object({
        type: z.enum(["QUICK_REPLY", "URL", "PHONE_NUMBER"]),
        text: z.string().min(1).max(25),
        url: z.string().url().optional().or(z.literal("")),
        phone_number: z.string().optional().or(z.literal("")),
      })
    )
    .max(3)
    .optional(),
  variableMapping: z
    .array(
      z.object({
        index: z.number(),
        label: z.string(),
        source: z.string().optional(),
      })
    )
    .optional(),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

interface TemplateFormProps {
  defaultValues?: Partial<TemplateFormValues>;
  onSubmit: (data: CreateTemplateInput) => void;
  isLoading?: boolean;
  isEdit?: boolean;
}

function extractVariables(text: string): number[] {
  const matches = [...text.matchAll(/\{\{(\d+)\}\}/g)];
  const indices = matches.map((m) => Number(m[1]));
  return [...new Set(indices)].sort((a, b) => a - b);
}

export function TemplateForm({ defaultValues, onSubmit, isLoading, isEdit }: TemplateFormProps) {
  const { t } = useTranslation("whatsappTemplates");
  const [detectedVars, setDetectedVars] = useState<number[]>([]);

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: "",
      language: "pt_BR",
      category: "MARKETING",
      headerType: "",
      headerText: "",
      headerMediaUrl: "",
      bodyText: "",
      footerText: "",
      buttons: [],
      variableMapping: [],
      ...defaultValues,
    },
  });

  const { fields: buttonFields, append: appendButton, remove: removeButton } = useFieldArray({
    control: form.control,
    name: "buttons",
  });

  const { fields: varFields, replace: replaceVars } = useFieldArray({
    control: form.control,
    name: "variableMapping",
  });

  const bodyText = form.watch("bodyText");
  const headerText = form.watch("headerText");
  const headerType = form.watch("headerType");
  const footerText = form.watch("footerText");
  const buttons = form.watch("buttons");
  const category = form.watch("category");

  useEffect(() => {
    const allText = [bodyText, headerText].filter(Boolean).join(" ");
    const vars = extractVariables(allText);
    setDetectedVars(vars);
    const current = form.getValues("variableMapping") ?? [];
    const updated: TemplateVariableMapping[] = vars.map((idx) => {
      const existing = current.find((v) => v.index === idx);
      return { index: idx, label: existing?.label ?? "", source: existing?.source ?? "" };
    });
    // Sync when variables change (indices or count differ)
    const currentIndices = current.map((v) => v.index).sort().join(",");
    const updatedIndices = vars.sort((a, b) => a - b).join(",");
    if (currentIndices !== updatedIndices) {
      replaceVars(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bodyText, headerText]);

  function insertVariable(fieldName: "bodyText" | "headerText") {
    const next = detectedVars.length > 0 ? Math.max(...detectedVars) + 1 : 1;
    const current = form.getValues(fieldName) ?? "";
    form.setValue(fieldName, current + `{{${next}}}`);
  }

  function handleSubmit(values: TemplateFormValues) {
    const input: CreateTemplateInput = {
      name: values.name,
      language: values.language,
      category: values.category,
      bodyText: values.bodyText,
      footerText: values.footerText || undefined,
      headerType: values.headerType || undefined,
      headerText: values.headerText || undefined,
      headerMediaUrl: values.headerMediaUrl || undefined,
      buttons: (values.buttons ?? []).filter((b) => b.text) as TemplateButton[],
      variableMapping: values.variableMapping as TemplateVariableMapping[],
    };
    onSubmit(input);
  }

  const previewTemplate = {
    bodyText: bodyText ?? "",
    footerText: footerText || undefined,
    headerType: (headerType as "TEXT" | undefined) || undefined,
    headerText: headerText || undefined,
    buttons: (buttons ?? []) as TemplateButton[],
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
          {/* Name */}
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("formNameLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("formNamePlaceholder")} {...field} disabled={isEdit} />
                </FormControl>
                <p className="text-xs text-muted-foreground">{t("formNameHint")}</p>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Category + Language row */}
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("formCategoryLabel")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="MARKETING">
                        {t("categoryMarketing")} — R$ {META_PRICING_BRL.MARKETING.toFixed(2)}
                      </SelectItem>
                      <SelectItem value="UTILITY">
                        {t("categoryUtility")} — R$ {META_PRICING_BRL.UTILITY.toFixed(2)}
                      </SelectItem>
                      <SelectItem value="AUTHENTICATION">
                        {t("categoryAuthentication")} — R$ {META_PRICING_BRL.AUTHENTICATION.toFixed(2)}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("formLanguageLabel")}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="pt_BR">{t("formLanguagePt")}</SelectItem>
                      <SelectItem value="en_US">{t("formLanguageEn")}</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Pricing info */}
          <p className="text-xs text-muted-foreground">
            {t("pricingInfo", { price: META_PRICING_BRL[category].toFixed(2) })}
          </p>

          {/* Header type */}
          <FormField
            control={form.control}
            name="headerType"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("headerSection")}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value ?? ""}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder={t("headerTypeNone")} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="">{t("headerTypeNone")}</SelectItem>
                    <SelectItem value="TEXT">{t("headerTypeText")}</SelectItem>
                    <SelectItem value="IMAGE">{t("headerTypeImage")}</SelectItem>
                    <SelectItem value="VIDEO">{t("headerTypeVideo")}</SelectItem>
                    <SelectItem value="DOCUMENT">{t("headerTypeDocument")}</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {headerType === "TEXT" && (
            <FormField
              control={form.control}
              name="headerText"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{t("formHeaderTextLabel")}</FormLabel>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => insertVariable("headerText")}
                    >
                      {t("insertVariable")}
                    </Button>
                  </div>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {headerType && headerType !== "TEXT" && (
            <FormField
              control={form.control}
              name="headerMediaUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("formHeaderMediaLabel")}</FormLabel>
                  <FormControl>
                    <Input type="url" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Body */}
          <FormField
            control={form.control}
            name="bodyText"
            render={({ field }) => (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>{t("formBodyLabel")}</FormLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => insertVariable("bodyText")}
                  >
                    {t("insertVariable")}
                  </Button>
                </div>
                <FormControl>
                  <Textarea
                    rows={4}
                    placeholder={t("formBodyPlaceholder")}
                    {...field}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">{t("formBodyHint")}</p>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Footer */}
          <FormField
            control={form.control}
            name="footerText"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("formFooterLabel")}</FormLabel>
                <FormControl>
                  <Input placeholder={t("formFooterPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Variable mapping */}
          {detectedVars.length > 0 && (
            <div className="space-y-2">
              <Label>{t("variableMappingSection")}</Label>
              <p className="text-xs text-muted-foreground">{t("variableMappingDesc")}</p>
              {varFields.map((field, i) => (
                <div key={field.id} className="flex items-center gap-2">
                  <span className="text-xs w-20 shrink-0 font-mono text-muted-foreground">
                    {`{{${detectedVars[i]}}}`}
                  </span>
                  <Input
                    {...form.register(`variableMapping.${i}.label`)}
                    placeholder={t("variablePlaceholder")}
                    className="text-xs"
                  />
                </div>
              ))}
            </div>
          )}

          {/* Buttons */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{t("buttonsSection")}</Label>
              {buttonFields.length < 3 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => appendButton({ type: "QUICK_REPLY", text: "" })}
                >
                  <Plus className="h-3 w-3" />
                  {t("addButton")}
                </Button>
              )}
            </div>
            {buttonFields.map((field, i) => (
              <div key={field.id} className="flex items-start gap-2">
                <Select
                  value={form.watch(`buttons.${i}.type`)}
                  onValueChange={(v) => form.setValue(`buttons.${i}.type`, v as "QUICK_REPLY" | "URL" | "PHONE_NUMBER")}
                >
                  <SelectTrigger className="w-36 shrink-0 text-xs h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="QUICK_REPLY">{t("buttonTypeQuickReply")}</SelectItem>
                    <SelectItem value="URL">{t("buttonTypeUrl")}</SelectItem>
                    <SelectItem value="PHONE_NUMBER">{t("buttonTypePhone")}</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  {...form.register(`buttons.${i}.text`)}
                  placeholder={t("buttonLabel")}
                  className="text-xs"
                  inputSize="sm"
                />
                {form.watch(`buttons.${i}.type`) === "URL" && (
                  <Input
                    {...form.register(`buttons.${i}.url`)}
                    placeholder={t("buttonUrl")}
                    className="text-xs"
                    inputSize="sm"
                  />
                )}
                {form.watch(`buttons.${i}.type`) === "PHONE_NUMBER" && (
                  <Input
                    {...form.register(`buttons.${i}.phone_number`)}
                    placeholder={t("buttonPhone")}
                    className="text-xs"
                    inputSize="sm"
                  />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => removeButton(i)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex justify-end pt-2">
            <Button type="submit" disabled={isLoading}>
              {isEdit ? t("submitUpdateLabel") : t("submitCreateLabel")}
            </Button>
          </div>
        </form>
      </Form>

      {/* Preview */}
      <div className="hidden lg:flex items-start justify-center pt-8">
        <TemplatePreview template={previewTemplate} />
      </div>
    </div>
  );
}
